/**
 * Scroll Animations — Apple-style entrance effects
 *
 * Automatically detects common Shopify section elements and wires them
 * up with IntersectionObserver-driven CSS transitions. No per-section
 * template changes needed.
 *
 * Features:
 *  - Fade-up / scale-up / slide reveals triggered on scroll
 *  - Staggered grid children (product cards, icon columns, etc.)
 *  - Subtle parallax on rich-text images
 *  - Section heading cascade (heading → subheading)
 *  - Respects `prefers-reduced-motion`
 */

(function () {
  'use strict';

  /* ---- Early exit if user prefers reduced motion ---- */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* ---- Configuration ---- */
  var THRESHOLD  = 0.15;   // 15 % of element visible before triggering
  var ROOT_MARGIN = '0px 0px -60px 0px'; // slight bottom inset so animations start just before center

  /* ========================================================================
     1. Auto-annotate: walk the DOM and add data-sa attributes
     ======================================================================== */

  function annotateElements () {
    /* --- Section headings --- */
    document.querySelectorAll('.section-heading').forEach(function (el) {
      if (!el.closest('.shopify-section-header')) {
        el.setAttribute('data-sa', 'fade-up');
      }
    });

    /* --- Rich-text sections --- */
    document.querySelectorAll('.rich-text').forEach(function (card) {
      // Image half — slide from its side
      var img = card.querySelector('.rich-text__image');
      if (img) img.setAttribute('data-sa', 'scale-up');

      // Text half — stagger its children
      var text = card.querySelector('.card__text');
      if (text) {
        text.setAttribute('data-sa-stagger', '');
        // also observe it so we know when it enters
        text.setAttribute('data-sa', 'fade-in');
      }
    });

    /* --- Text columns with icons (grid children) --- */
    document.querySelectorAll('.mount-css-slider .grid, .mount-css-slider .grid--slider').forEach(function (grid) {
      // Skip if it's inside the header
      if (grid.closest('.shopify-section-header')) return;
      grid.setAttribute('data-sa-stagger', '');
      Array.prototype.forEach.call(grid.children, function (child) {
        if (!child.hasAttribute('data-sa')) {
          child.setAttribute('data-sa', 'fade-up');
        }
      });
    });

    /* --- Featured-collection product items --- */
    document.querySelectorAll('.mount-collection-tabs .grid').forEach(function (grid) {
      grid.setAttribute('data-sa-stagger', '');
      grid.querySelectorAll('.product-item').forEach(function (item) {
        item.setAttribute('data-sa', 'fade-up');
      });
    });

    /* --- Grid-layout cards (promotion cards, flex cards, etc.) --- */
    document.querySelectorAll('.grid--layout').forEach(function (grid) {
      if (grid.closest('.shopify-section-header')) return;
      if (grid.closest('.mount-collection-tabs') || grid.closest('.mount-css-slider')) return;
      grid.setAttribute('data-sa-stagger', '');
      Array.prototype.forEach.call(grid.children, function (child) {
        if (!child.hasAttribute('data-sa')) {
          child.setAttribute('data-sa', 'fade-up');
        }
      });
    });

    /* --- Media-with-text-overlay items --- */
    document.querySelectorAll('.media-with-text-overlay .grid > *, .media-with-text-overlay__item').forEach(function (item) {
      if (!item.hasAttribute('data-sa')) {
        item.setAttribute('data-sa', 'fade-up');
      }
    });

    /* --- Testimonial items --- */
    document.querySelectorAll('.testimonial').forEach(function (item) {
      item.setAttribute('data-sa', 'fade-up');
    });

    /* --- Flex-slideshow captions --- */
    document.querySelectorAll('.flex-slideshow__text').forEach(function (el) {
      el.setAttribute('data-sa', 'fade-up-sm');
    });

    /* --- Scrolling images section heading (already scrolls, so just fade) --- */
    document.querySelectorAll('.scrolling-images .section-heading').forEach(function (el) {
      el.setAttribute('data-sa', 'fade-up');
    });

    /* --- Featured product section --- */
    document.querySelectorAll('.mount-featured-product .card__text, .mount-main-product .card__text').forEach(function (el) {
      if (!el.hasAttribute('data-sa-stagger')) {
        el.setAttribute('data-sa-stagger', '');
        el.setAttribute('data-sa', 'fade-in');
      }
    });

    /* --- Newsletter section --- */
    document.querySelectorAll('.mount-newsletter').forEach(function (section) {
      var inner = section.querySelector('.newsletter') || section.querySelector('.card');
      if (inner && !inner.hasAttribute('data-sa')) {
        inner.setAttribute('data-sa', 'fade-up-sm');
      }
    });

    /* --- Content toggles / FAQ --- */
    document.querySelectorAll('.content-toggles').forEach(function (el) {
      el.setAttribute('data-sa', 'fade-up-sm');
    });

    /* --- Generic fallback: any `.card` inside a shopify-section that isn't annotated --- */
    document.querySelectorAll('.shopify-section > div > .card:not(.rich-text)').forEach(function (card) {
      if (!card.hasAttribute('data-sa') && !card.querySelector('[data-sa]')) {
        card.setAttribute('data-sa', 'fade-up-sm');
      }
    });
  }

  /* ========================================================================
     2. IntersectionObserver — trigger `.is-visible`
     ======================================================================== */

  function createObserver () {
    return new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target); // once only
        }
      });
    }, {
      threshold: THRESHOLD,
      rootMargin: ROOT_MARGIN
    });
  }

  var observer = createObserver();

  function observeAll () {
    document.querySelectorAll('[data-sa]').forEach(function (el) {
      // Skip elements that are already visible (e.g. above fold on load)
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.85 && rect.bottom > 0) {
        // Element is already in view — reveal immediately (no jarring pop)
        el.classList.add('is-visible');
      } else {
        observer.observe(el);
      }
    });
  }

  /* ========================================================================
     3. Parallax — subtle depth on rich-text images
     ======================================================================== */

  var parallaxEls = [];
  var ticking = false;

  function setupParallax () {
    document.querySelectorAll('.rich-text__image').forEach(function (el) {
      // Only apply to images that have actual content
      if (el.querySelector('img') || el.querySelector('.lazy-image')) {
        el.setAttribute('data-sa-parallax', '');
        parallaxEls.push(el);
      }
    });
  }

  function updateParallax () {
    var winH = window.innerHeight;
    parallaxEls.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      // Only process if in viewport
      if (rect.bottom < -100 || rect.top > winH + 100) return;
      // Progress: 0 when element enters from bottom, 1 when it leaves at top
      var progress = (winH - rect.top) / (winH + rect.height);
      // Map to a subtle translate range: -20px to +20px
      var offset = (progress - 0.5) * 40;
      var target = el.querySelector('img') || el.querySelector('.lazy-image');
      if (target) {
        target.style.transform = 'translateY(' + offset.toFixed(1) + 'px) scale(1.05)';
      }
    });
    ticking = false;
  }

  function onScroll () {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(updateParallax);
    }
  }

  /* ========================================================================
     4. Init — run after DOM is ready
     ======================================================================== */

  function init () {
    annotateElements();
    observeAll();
    setupParallax();

    if (parallaxEls.length > 0) {
      window.addEventListener('scroll', onScroll, { passive: true });
      updateParallax(); // initial position
    }
  }

  /* ========================================================================
     5. Handle Shopify section events (theme editor live-reload)
     ======================================================================== */

  function onSectionLoad () {
    // Re-scan for new elements after Shopify injects a section
    setTimeout(function () {
      annotateElements();
      observeAll();
      setupParallax();
    }, 100);
  }

  document.addEventListener('shopify:section:load', onSectionLoad);

  /* ---- Kick off ---- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
